import { Card, Flex, Text, VStack } from '@chakra-ui/react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { toaster } from '~/components/ui/toaster'

import {
  type FieldSchema,
  getOAuthUrlForPartner,
  getSchemaForPartner,
} from '@mangovoice/grove-scheduler-client'
import { Checkbox, CopyButton } from '@mangovoice/mango-ui'
import { getIanaTimezone } from '@mangovoice/reporting-ui'

import { IntegrationAgentAuthSection } from './IntegrationAgentAuthSection'

export interface IntegrationAgentCardProps {
  partner: string
  agentId: string
  config?: Record<string, string | boolean>
  active?: boolean
  createdAt?: string
  updatedAt?: string
}

/**
 * Integration agent card showing configuration, status, and authorization
 * Customers can view their config to verify with integration partners
 */
export const IntegrationAgentCard: React.FC<IntegrationAgentCardProps> = ({
  partner,
  agentId,
  config = {},
  active = true,
  createdAt,
  updatedAt,
}) => {
  const { t } = useTranslation()
  const schema = getSchemaForPartner(partner)
  const oauthUrl = schema ? getOAuthUrlForPartner(partner, agentId) : null

  if (!schema) return null

  const statusColor = active ? 'green' : 'yellow'
  const statusLabel = active
    ? t('integrationsSettings.statusActive')
    : t('integrationsSettings.statusPaused')

  const formatTimestamp = (timestamp?: string) => {
    if (!timestamp) return t('integrationsSettings.unknownDate')
    const date = new Date(timestamp)
    const timezone = getIanaTimezone()
    return date.toLocaleString(undefined, { timeZone: timezone })
  }

  const renderFieldValue = (
    field: FieldSchema,
    value: string | boolean | undefined,
  ) => {
    // Handle checkbox fields - render as inline checkbox with label
    if (field.type === 'checkbox') {
      const isChecked = value === true || value === 'true'
      return (
        <Checkbox checked={isChecked} disabled cursor="default">
          {field.label}
        </Checkbox>
      )
    }

    // Handle text fields - render label + value in monospace box
    return (
      <Flex direction="column" gap={1}>
        <Text fontSize="sm" fontWeight="medium" color="fg.default">
          {field.label}
        </Text>
        <Text
          fontSize="sm"
          color="fg.muted"
          fontFamily="mono"
          bg="bg.muted"
          px={3}
          py={2}
          borderRadius="md"
        >
          {value?.toString() || t('integrationsSettings.notSet')}
        </Text>
      </Flex>
    )
  }

  return (
    <Card.Root>
      <Card.Header p={6}>
        <Flex justify="space-between" align="center">
          <VStack gap={0} align="stretch">
            <Text fontSize="lg" fontWeight="bold">
              {schema.displayName}
            </Text>
            <CopyButton
              value={agentId}
              text={t('integrationsSettings.agentId', { id: agentId })}
              copiedText={t('integrationsSettings.copiedConfirmation')}
              size="xs"
              variant="ghost"
              iconSize={12}
              color="fg.muted"
              fontFamily="mono"
              fontSize="xs"
              height="auto"
              minH="auto"
              px={1}
              onCopyError={() =>
                toaster.create({
                  title: t('integrationsSettings.copyIdFailedTitle'),
                  type: 'warning',
                  duration: 4000,
                })
              }
            />
          </VStack>
          <Flex gap={2} align="center">
            <Text
              fontSize="xs"
              fontWeight="semibold"
              px={2}
              py={1}
              borderRadius="md"
              bg={`${statusColor}.100`}
              color={`${statusColor}.800`}
            >
              {statusLabel}
            </Text>
          </Flex>
        </Flex>
      </Card.Header>
      <Card.Body p={6}>
        <VStack gap={3} align="stretch">
          {/* Configuration fields */}
          {schema.fields.map((field, index) => (
            <React.Fragment key={index}>
              {renderFieldValue(field, config[field.key])}
            </React.Fragment>
          ))}

          {/* Timestamps in same style as config fields */}
          <Flex direction="column" gap={1}>
            <Text fontSize="sm" fontWeight="medium" color="fg.default">
              {t('integrationsSettings.createdLabel')}
            </Text>
            <Text
              fontSize="sm"
              color="fg.muted"
              fontFamily="mono"
              bg="bg.muted"
              px={3}
              py={2}
              borderRadius="md"
            >
              {formatTimestamp(createdAt)}
            </Text>
          </Flex>

          <Flex direction="column" gap={1}>
            <Text fontSize="sm" fontWeight="medium" color="fg.default">
              {t('integrationsSettings.updatedLabel')}
            </Text>
            <Text
              fontSize="sm"
              color="fg.muted"
              fontFamily="mono"
              bg="bg.muted"
              px={3}
              py={2}
              borderRadius="md"
            >
              {formatTimestamp(updatedAt)}
            </Text>
          </Flex>

          {/* Authorization Section - for OAuth partners (shows fallback if no URL builder) */}
          {schema.requiresOAuth && (
            <IntegrationAgentAuthSection oauthUrl={oauthUrl} schema={schema} />
          )}
        </VStack>
      </Card.Body>
    </Card.Root>
  )
}
